import React, { useState, useEffect } from "react";
import { Layout, Table, Button, Space, Descriptions, Card, Tag, message, Row, Col, Modal, Tooltip, Timeline } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { ArrowLeftOutlined, FileTextOutlined, PrinterOutlined, DownloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, SendOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import { getAuthItem } from "../../../services/authService";
import dayjs from "dayjs";
import "../../../styles/fluidDesign.css";
import MRPreviewModal from "../../../components/material_requests/MRPreviewModal";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;

const ViewRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mrMaster, setMrMaster] = useState(null);
  const [mrItems, setMrItems] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);

  useEffect(() => {
    fetchMRDetails();
  }, [id]);

  const fetchMRDetails = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inventory/material_request/get.php?mr_id=${id}`);
      if (res.data && res.data.status === "success" && res.data.dataset) {
        setMrMaster(res.data.dataset);
        setMrItems(res.data.dataset.items || []);
      } else {
        message.error("Material Request not found.");
      }
    } catch (error) {
      message.error("Failed to load Material Request details.");
    } finally {
      setLoading(false);
    }
  };

  const getTimelineItems = () => {
    if (!mrMaster) return [];
    const rawItems = [];
    const status = mrMaster.req_status;

    // Step 1: Created
    rawItems.push({
      statusName: 'Material Request Created',
      statusLabel: 'Draft',
      color: '#2d5a3d',
      dotIcon: <ClockCircleOutlined style={{ color: '#2d5a3d' }} />,
      by: mrMaster.creator_name,
      date: mrMaster.req_dt ? dayjs(mrMaster.req_dt).format('YYYY-MM-DD hh:mm:ss A') : '',
      comment: null
    });

    // Step 2: Decision
    const reviewDateStr = mrMaster.req_approved_dt ? dayjs.unix(Number(mrMaster.req_approved_dt)).format('YYYY-MM-DD hh:mm:ss A') : '';
    if (status === 'Approved' || status === 'Completed' || status === 'Issued') {
      rawItems.push({
        statusName: 'Request Approved',
        statusLabel: 'Approved',
        color: '#52c41a',
        dotIcon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
        by: mrMaster.reviewer_name,
        date: reviewDateStr,
        comment: null
      });
    } else if (status === 'Rejected') {
      rawItems.push({
        statusName: 'Request Rejected',
        statusLabel: 'Rejected',
        color: '#ff4d4f',
        dotIcon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
        by: mrMaster.reviewer_name,
        date: reviewDateStr,
        comment: null
      });
    } else if (status === 'Pending' || status === 'Pending Approval') {
      rawItems.push({
        statusName: 'Awaiting Review',
        statusLabel: 'Pending',
        color: '#8c8c8c',
        dotIcon: <ClockCircleOutlined style={{ color: '#8c8c8c' }} />,
        by: null,
        date: '',
        comment: null
      });
    }

    const items = rawItems.map((item, idx) => {
      const stepNum = idx + 1;
      return {
        color: item.color,
        dot: (
          <div style={{
            padding: '4px',
            background: item.color === '#52c41a' ? '#f6ffed' : (item.color === '#ff4d4f' ? '#fff1f0' : '#f9f9f9'),
            borderRadius: '50%',
            border: `1px solid ${item.color}`
          }}>
            {item.dotIcon}
          </div>
        ),
        children: (
          <div style={{
            background: '#f9f9f9',
            padding: '12px 16px',
            borderRadius: '8px',
            borderLeft: `4px solid ${item.color}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>{item.statusName}</span>
              <Tag color={getStatusColor(item.statusLabel)}>Step {stepNum}</Tag>
            </div>
            {item.by && (
              <div style={{ fontSize: '13px', color: '#555' }}>
                By: <span style={{ fontWeight: 500 }}>{item.by}</span>
              </div>
            )}
            {item.date && (
              <div style={{ fontSize: '12px', color: '#888' }}>
                {item.date}
              </div>
            )}
          </div>
        )
      };
    });

    return items.reverse();
  };

  const handleDelete = async () => {
    Modal.confirm({
      title: "Delete Draft",
      content: "Are you sure you want to delete this draft request? This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          const response = await api.post("/inventory/material_request/delete.php", { mr_id: Number(id) });
          if (response.data.status === "success") {
            message.success(response.data.message || "Draft request deleted successfully.");
            navigate("/inventory/material-request/list");
          } else {
            message.error(response.data.message || "Failed to delete draft request.");
          }
        } catch (error) {
          message.error("Error occurred while deleting.");
        }
      }
    });
  };

  const renderStatusTag = (status) => {
    return <Tag color={getStatusColor(status)} style={{ fontWeight: "600" }}>{status}</Tag>;
  };

  return (
    <Layout className="fluid-page">
      <Headerdiv />

      <div className="fluid-page-header">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <AppBreadcrumb items={[
          { label: 'Inventory', link: '/inventory-items' },
          { label: 'View Request' },
        ]} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 className="fluid-page-title" style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: 12 }} />
              Material Request: {mrMaster ? mrMaster.mr_ref : "Loading..."}
            </h1>
          </div>
          <div className="hero-actions" style={{ marginTop: 0, display: "flex", gap: "12px", alignItems: "center" }}>
            {mrMaster && mrMaster.req_status === 'Draft' && getAuthItem("USERPERMISSION_material_request_create") === "true" && (
              <>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/inventory/material-request/${id}/items`)}
                  className="hero-action-btn"
                >
                  Edit Draft
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                  className="hero-action-btn"
                >
                  Delete Draft
                </Button>
              </>
            )}
            {mrMaster && (
              <Tooltip title="Print">
                <Button
                  icon={<PrinterOutlined />}
                  onClick={() => setPreviewVisible(true)}
                  className="fluid-btn-white"
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      <Content className="fluid-content" style={{ minHeight: "calc(100vh - 350px)" }}>
        {mrMaster && (
          <Space direction="vertical" size="large" style={{ display: 'flex', width: '100%' }}>
            <Card className="fluid-card" title="Request Overview">
              <Descriptions bordered column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }} size="small">
                <Descriptions.Item label="Request Reference"><strong>{mrMaster.mr_ref}</strong></Descriptions.Item>
                <Descriptions.Item label="Required Date">{dayjs(mrMaster.required_date).format("YYYY-MM-DD")}</Descriptions.Item>
                <Descriptions.Item label="Status">{renderStatusTag(mrMaster.req_status)}</Descriptions.Item>
                <Descriptions.Item label="Created By">{mrMaster.creator_name ? mrMaster.creator_name.charAt(0).toUpperCase() + mrMaster.creator_name.slice(1) : "-"}</Descriptions.Item>
                <Descriptions.Item label="Requested Date">{dayjs(mrMaster.req_dt).format("YYYY-MM-DD hh:mm:ss A")}</Descriptions.Item>
                {mrMaster.reviewer_name && (
                  <>
                    <Descriptions.Item label="Reviewed By">{mrMaster.reviewer_name ? mrMaster.reviewer_name.charAt(0).toUpperCase() + mrMaster.reviewer_name.slice(1) : "-"}</Descriptions.Item>
                    <Descriptions.Item label="Review Date">
                      {mrMaster.req_approved_dt
                        ? dayjs.unix(Number(mrMaster.req_approved_dt)).format("YYYY-MM-DD hh:mm:ss A")
                        : "-"}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </Card>

            <Card className="fluid-card" title="Requested Items List" loading={loading}>
              <Table
                dataSource={mrItems}
                rowKey="mri_id"
                pagination={false}
                bordered
                size="middle"
                scroll={{ x: "max-content" }}
                columns={[
                  { title: "Item Code", dataIndex: "in_code", key: "in_code" },
                  { title: "Item Name", dataIndex: "in_name", key: "in_name" },
                  { title: "UOM", dataIndex: "uom_name", key: "uom_name", render: (val) => val || "-" },
                  { title: "Requested Qty", dataIndex: "request_qty", key: "request_qty", align: "right", render: (qty) => <strong>{parseFloat(qty || 0).toFixed(2)}</strong> },
                  { title: "Approved Qty", dataIndex: "accept_qty", key: "accept_qty", align: "right", render: (qty) => parseFloat(qty || 0).toFixed(2) },
                  { title: "Issued Qty", dataIndex: "issue_qty", key: "issue_qty", align: "right", render: (qty) => parseFloat(qty || 0).toFixed(2) },
                  { title: "Remaining Qty", dataIndex: "remain_qty", key: "remain_qty", align: "right", render: (qty) => parseFloat(qty || 0).toFixed(2) },
                  { title: "Reason for Use", dataIndex: "reason_for_used", key: "reason_for_used", render: (val) => val || "-" }
                ]}
              />
            </Card>

            <Card className="fluid-card" title={<><ClockCircleOutlined style={{ marginRight: 8 }} /> Approval History</>}>
              <div style={{ padding: '24px 12px' }}>
                <Timeline mode="left" items={getTimelineItems()} />
              </div>
            </Card>
          </Space>
        )}
      </Content>
      <MRPreviewModal
        mrId={id}
        visible={previewVisible}
        onCancel={() => setPreviewVisible(false)}
      />
      <Footerdiv />
    </Layout>
  );
};

export default ViewRequest;
